/**
 * Gameplay Constants
 * All gameplay-related values: soldiers, weapons, enemies, scoring.
 * Values derived from PRD.md specifications.
 */

import { GAME_HEIGHT } from '../config';
import { getShieldHpForLevel } from '../levels/gameplayBalance';

// =============================================================================
// COLLISION CATEGORIES (for physics bitmasks)
// =============================================================================

/** Collision categories for physics bodies (power of 2 for bitmask operations) */
export const COLLISION_CATEGORY = {
  player: 1,
  bullet: 2,
  enemy: 4,
  gate: 8,
  weaponBox: 16,
} as const;

// =============================================================================
// PLAYER ARMY
// =============================================================================

export const GAMEPLAY_START = {
  /** Number of soldiers at level start across all game modes/levels */
  startingSoldiers: 1,
  /** Base HP at level start */
  baseHp: 5,
} as const;

/**
 * Longer missions naturally expose the shield to more lanes and leak chances.
 * Scaling durability by level keeps leakage a warning system instead of making
 * track length itself an invisible difficulty multiplier.
 */
export function getBaseHpForLevel(level: number, tier: number = 1): number {
  return getShieldHpForLevel(level, tier);
}

export const ARMY = {
  /** Number of soldiers at level start */
  startingSoldiers: GAMEPLAY_START.startingSoldiers,

  /** Weapon range as percentage of screen height */
  weaponRangePercent: 1.0,

  /** Weapon range in pixels */
  weaponRange: Math.round(GAME_HEIGHT * 1.0),

  /** Formation breathing interval in ms */
  formationBreathInterval: 500,

  /** Max random offset for soldier formation */
  formationJitter: 5,
} as const;

// =============================================================================
// SOFT CAP MULTIPLIERS
// =============================================================================

/** Hard cap - absolute maximum army size */
export const ARMY_HARD_CAP = 240;

/** Multiplier effectiveness based on army size (aggressive diminishing returns) */
export const SOFT_CAP = {
  /** Full effect threshold - lower to make early gates more strategic */
  fullEffectMax: 60,
  /** 50% effect range */
  reducedEffect50Max: 120,
  /** 25% effect range */
  reducedEffect25Max: 180,
  /** 10% effect range */
  reducedEffect10Max: 220,

  /** Multiplier rates - more aggressive falloff */
  fullRate: 1.0,
  rate50: 0.60,
  rate25: 0.35,
  rate10: 0.20,
  rate05: 0.10,
} as const;

/**
 * Calculate effective multiplier based on current army size
 * Uses aggressive logarithmic-style scaling to prevent runaway growth
 */
export function getEffectiveMultiplier(armySize: number, gateMultiplier: number): number {
  // If already at hard cap, no growth
  if (armySize >= ARMY_HARD_CAP) {
    return armySize;
  }

  let rate: number;

  if (armySize <= SOFT_CAP.fullEffectMax) {
    rate = SOFT_CAP.fullRate;
  } else if (armySize <= SOFT_CAP.reducedEffect50Max) {
    rate = SOFT_CAP.rate50;
  } else if (armySize <= SOFT_CAP.reducedEffect25Max) {
    rate = SOFT_CAP.rate25;
  } else if (armySize <= SOFT_CAP.reducedEffect10Max) {
    rate = SOFT_CAP.rate10;
  } else {
    rate = SOFT_CAP.rate05;
  }

  // Formula: current + (current × rate × (multiplier - 1))
  // e.g., 60 soldiers through 2x gate with 0.25 rate = 60 + (60 × 0.25 × 1) = 75
  const newSize = Math.floor(armySize + (armySize * rate * (gateMultiplier - 1)));

  // Enforce hard cap
  return Math.min(newSize, ARMY_HARD_CAP);
}

// =============================================================================
// WEAPONS
// =============================================================================

export enum WeaponType {
  Pistol = 'pistol',
  Rifle = 'rifle',
  MachineGun = 'machineGun',
  Rocket = 'rocket',
}

export interface WeaponConfig {
  type: WeaponType;
  fireRate: number;      // Shots per second
  damage: number;
  isAOE: boolean;
  aoeTargets?: number;   // Number of enemies hit by AOE
}

export const WEAPONS: Record<WeaponType, WeaponConfig> = {
  [WeaponType.Pistol]: {
    type: WeaponType.Pistol,
    fireRate: 3,
    damage: 1,
    isAOE: false,
  },
  [WeaponType.Rifle]: {
    type: WeaponType.Rifle,
    fireRate: 4.5,
    damage: 1,
    isAOE: false,
  },
  [WeaponType.MachineGun]: {
    type: WeaponType.MachineGun,
    fireRate: 7,
    damage: 1,
    isAOE: false,
  },
  [WeaponType.Rocket]: {
    type: WeaponType.Rocket,
    fireRate: 1.25,
    damage: 3,
    isAOE: true,
    aoeTargets: 3,
  },
} as const;

/** Weapon unlock order */
export const WEAPON_SEQUENCE: WeaponType[] = [
  WeaponType.Pistol,
  WeaponType.Rifle,
  WeaponType.MachineGun,
  WeaponType.Rocket,
];

// =============================================================================
// ENEMIES
// =============================================================================

export const ENEMIES = {
  basic: {
    hp: 1,
    points: 10,
    speed: 1.0,  // Relative speed multiplier
  },
  elite: {
    minHp: 5,
    // Finale targets share the elite presentation but need enough health to
    // survive a large squad for several seconds. Regular elites remain capped
    // by the much smaller values produced by LevelGenerator.computeEliteHp().
    maxHp: 600,
    /** Points per HP */
    pointsPerHp: 10,
    speed: 1.1,
    /** First level elites appear */
    introLevel: 1,
  },
  /** Collision damage multiplier vs enemy HP */
  collisionDamageMultiplier: 1.2,
} as const;

// =============================================================================
// GATES
// =============================================================================

export const GATES = {
  /** Available multiplier values */
  multipliers: [2, 3, 5] as const,

  /** Glow animation settings */
  pulseOpacityMin: 0.4,
  pulseOpacityMax: 0.8,
} as const;

// =============================================================================
// WEAPON BOXES
// =============================================================================

export const WEAPON_BOX = {
  /** Default HP for weapon boxes */
  defaultHp: 32,
} as const;

// =============================================================================
// SCORING
// =============================================================================

export const SCORING = {
  /** Points per basic enemy */
  basicEnemyPoints: 10,
  /** Elite enemy points = HP × this multiplier */
  elitePointsMultiplier: 10,
  /** Bonus points per surviving soldier at level end */
  soldierBonusPoints: 5,
} as const;

// =============================================================================
// LEVEL PACING
// =============================================================================

export const LEVEL_PACING = {
  /** Target level duration range in seconds */
  minDurationSeconds: 30,
  maxDurationSeconds: 60,
  /** Gate decisions per level (fewer = more strategic) */
  minGates: 3,
  maxGates: 5,
  /** Enemy waves per level (more = consistent pressure) */
  minWaves: 5,
  maxWaves: 7,
  /** Weapon box opportunities per level */
  minWeaponBoxes: 1,
  maxWeaponBoxes: 2,
} as const;

// =============================================================================
// SCROLL SPEEDS
// =============================================================================

export const SCROLL_SPEED = {
  /** Base scroll speed (pixels per second) */
  base: 80,
  /** Speed multipliers by level */
  levelMultipliers: {
    1: 1.0,
    2: 1.0,
    3: 1.1,
    4: 1.1,
    5: 1.2,
    6: 1.2,
    7: 1.3,
    8: 1.3,
    9: 1.4,
    10: 1.5,
  } as Record<number, number>,
  /** Tier speed increase (each tier after 1 adds this) */
  tierMultiplier: 0.2,
} as const;

// =============================================================================
// TIER DIFFICULTY SCALING
// =============================================================================

/**
 * Scaling factors for higher tiers
 * Tier 1 = base difficulty, Tier 2+ gets progressively harder
 */
export const TIER_SCALING = {
  /** Extra HP added to basic enemies per tier (Tier 1 = 0, Tier 2 = 1, etc.) */
  basicEnemyHpBonus: 1,
  /** HP multiplier for elite enemies per tier (1.0 + tier bonus) */
  eliteHpMultiplier: 0.5, // Tier 2 = 1.5x, Tier 3 = 2.0x, etc.
  /** Points multiplier per tier (rewards scale with difficulty) */
  pointsMultiplier: 0.25, // Tier 2 = 1.25x, Tier 3 = 1.5x, etc.
} as const;

/**
 * Calculate tier-scaled HP for basic enemies
 */
export function getTierScaledBasicHp(tier: number): number {
  if (tier <= 1) return ENEMIES.basic.hp;
  return ENEMIES.basic.hp + (tier - 1) * TIER_SCALING.basicEnemyHpBonus;
}

/**
 * Calculate tier-scaled HP for elite enemies
 */
export function getTierScaledEliteHp(baseHp: number, tier: number): number {
  if (tier <= 1) return baseHp;
  const multiplier = 1 + (tier - 1) * TIER_SCALING.eliteHpMultiplier;
  return Math.ceil(baseHp * multiplier);
}

/**
 * Calculate tier-scaled points
 */
export function getTierScaledPoints(basePoints: number, tier: number): number {
  if (tier <= 1) return basePoints;
  const multiplier = 1 + (tier - 1) * TIER_SCALING.pointsMultiplier;
  return Math.floor(basePoints * multiplier);
}

// =============================================================================
// VISUAL EFFECTS
// =============================================================================

export const EFFECTS = {
  /** Screen shake on damage */
  screenShake: {
    duration: 200,
    intensity: 4,
    shakeCount: 4,
  },
  /** Enemy hit pulse (scale up then back) */
  enemyHitPulse: {
    scale: 1.12,
    duration: 80,
  },
  /** Enemy death pop (scale + slight rotation) */
  enemyDeathPop: {
    scale: 1.5,
    rotationMax: 0.2,
  },
  /** Elite death camera shake */
  eliteDeathShake: {
    duration: 140,
    intensity: 5,
  },
  /** Gate activation pop + camera shake */
  gateActivation: {
    popScale: 1.12,
    popDuration: 120,
    shakeDuration: 120,
    shakeIntensity: 2,
  },
  /** Gate choice lock pulse (selected gate) */
  gateChoiceLock: {
    scale: 1.06,
    duration: 120,
  },
  /** Enemy spawn pop-in scale */
  enemySpawnPop: {
    fromScale: 0.85,
    duration: 140,
  },
  /** Army movement dust trail */
  dustTrail: {
    minSpeed: 120, // px/s to start emitting
    spawnIntervalMs: 60,
    minSize: 2,
    maxSize: 4,
    minVelocity: 20,
    maxVelocity: 60,
    minDuration: 180,
    maxDuration: 260,
    alpha: 0.35,
  },
  /** Bullet impact sparks */
  hitSpark: {
    minParticles: 3,
    maxParticles: 6,
    minSize: 2,
    maxSize: 4,
    minVelocity: 60,
    maxVelocity: 120,
    minDuration: 120,
    maxDuration: 200,
    alpha: 0.8,
  },
  /** Bullet trail */
  bulletTrail: {
    lengthMultiplier: 3,  // Trail length = bullet width × this
    startOpacity: 0.3,
    endOpacity: 0,
  },
  /** Background parallax */
  parallax: {
    lineSpeedRatio: 0.5,  // Lines move at 50% of game scroll
    lineWidth: 2,
    lineSpacing: 60,
  },
} as const;

// =============================================================================
// INPUT
// =============================================================================

export const INPUT = {
  /** Maximum allowed input lag in ms (for responsiveness) */
  maxInputLag: 100,
} as const;
